import { ArrowUpRight, CheckCircle, XCircle, Medal, Trophy, Crown, Sparkle } from "@phosphor-icons/react";

const EVERFIT_URL = "https://invite.everfit.io/Q9TTSYVZYA";
const INTAKE_URL = "/intake";

const tiers = [
  {
    id: "essentials",
    rank: "01",
    medal: "Bronze",
    name: "Essentials",
    price: "$89",
    daily: "Less than $3/day",
    tagline: "The plan. The app. The freedom.",
    desc: "A simple, structured fitness plan for busy people who want results without the overwhelm. Perfect for self-starters who want expert guidance, flexibility, and a clear roadmap to follow at their own pace.",
    includes: [
      "Fitness assessment & onboarding",
      "1 end-of-week feedback check-in (week 1)",
      "Custom workout program delivered through Everfit",
      "Exercise demo videos",
      "Progress tracking + habit tracking",
      "Program updates every 4 weeks",
      "Monthly accountability check-in",
    ],
    excludes: [
      "Ongoing nutrition coaching",
      "Messaging support & coaching calls",
      "Weekly accountability check-ins",
      "Program changes outside monthly updates",
    ],
    perfectFor: "Busy professionals · Budget-conscious clients · Self-starters",
    icon: Medal,
    accent: false,
  },
  {
    id: "coaching",
    rank: "02",
    medal: "Silver",
    name: "Coaching",
    price: "$179",
    daily: "Less than $6/day",
    tagline: "Structure, feedback, support, accountability.",
    desc: "More than a workout plan. Whether you're starting, restarting, or finally trying to stay consistent — Coaching gives you the guidance, accountability, and personalized support to build sustainable habits.",
    includes: [
      "Everything in Essentials, plus:",
      "Initial strategy + goal-setting consultation",
      "Fully customized program for your goals, schedule, equipment & lifestyle",
      "Weekly accountability check-ins",
      "Messaging support between check-ins",
      "Basic nutrition guidance & habit coaching",
      "Form review & exercise feedback",
      "Program adjustments as needed",
    ],
    excludes: [],
    perfectFor: "Busy professionals · Beginners who want guidance · Frequent travelers · Anyone who wants a coach in their corner",
    icon: Trophy,
    accent: true,
    badge: "Most Popular",
  },
  {
    id: "vip",
    rank: "03",
    medal: "Gold",
    name: "VIP",
    price: "$399",
    daily: "Less than $14/day",
    tagline: "Your coach. Your partner. Your progress. No matter what.",
    desc: "The highest level of Sweat With Slay coaching and support. Hands-on experience, personalized attention, direct access, and a coach fully invested in your success.",
    includes: [
      "Everything in Coaching, plus:",
      "Monthly 1:1 coaching session (virtual or in-person if local)",
      "Priority messaging support",
      "Weekly voice note / video / call feedback",
      "Advanced nutrition + habit coaching",
      "Lifestyle coaching: sleep, stress, recovery, routine",
      "Real-time program adjustments",
      "Event-specific coaching (weddings, vacations, races, photoshoots)",
      "Quarterly comprehensive progress review",
      "Priority scheduling + direct access",
    ],
    excludes: [],
    perfectFor: "Busy executives · Frequent travelers · Clients prepping for major events · Anyone navigating big life changes",
    icon: Crown,
    accent: false,
  },
  {
    id: "bespoke",
    rank: "04",
    medal: "Bespoke",
    name: "Fully Customized",
    price: "Custom",
    daily: "Quoted to fit you",
    tagline: "Built from scratch. Priced for what you actually need.",
    desc: "For clients who don't fit a tier — multiple training disciplines, family / partner training, in-person blocks, event-prep camps, travel programming, or hybrid retainers. We build the scope, then quote it.",
    includes: [
      "Fully bespoke scope (training, nutrition, lifestyle, performance)",
      "In-person training blocks (if local) + remote programming",
      "Sport-specific work (lacrosse, pickleball, golf, running, etc.)",
      "Family / couples / partner training",
      "Multi-month event camps (wedding, race, photoshoot, season prep)",
      "On-demand programming for travel and seasons",
      "Pricing customized to your scope",
    ],
    excludes: [],
    perfectFor: "Clients with multi-faceted goals · Couples & families · Athletes · Anyone whose life doesn't fit a tier",
    icon: Sparkle,
    accent: false,
    isBespoke: true,
  },
];

export default function Programs() {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative px-6 md:px-12 lg:px-20 py-16 lg:py-20 border-b border-white/10"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#FF1B91]" />
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Programs &amp; Pricing / 03</p>
          </div>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tighter">
            4 simple choices.{" "}
            <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">one goal.</span>
          </h2>
        </div>
        <p className="md:max-w-sm text-white/70 leading-relaxed">
          Choose the level of support that fits your life, your goals, and how much accountability you want. All programs delivered through{" "}
          <span className="text-white font-semibold">Everfit</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {tiers.map((t) => {
          const Icon = t.icon;
          return (
            <div
              key={t.id}
              data-testid={`tier-card-${t.id}`}
              className={`group relative border ${
                t.accent
                  ? "border-[#FF1B91] bg-[#FF1B91]/[0.04]"
                  : t.isBespoke
                  ? "border-white/20 bg-gradient-to-br from-[#1a1a1a] to-[#0F0F0F]"
                  : "border-white/10 bg-[#0F0F0F]"
              } hover:border-[#FF1B91] transition-all duration-300 overflow-hidden flex flex-col`}
            >
              {t.badge && (
                <div className="absolute top-0 right-0 bg-[#FF1B91] text-white px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold">
                  {t.badge}
                </div>
              )}

              <div className="p-7 lg:p-8 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 border flex items-center justify-center transition-colors ${
                      t.accent ? "border-[#FF1B91] text-[#FF1B91]" : "border-white/15"
                    }`}>
                      <Icon size={20} weight="bold" />
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.3em] text-white/50">Tier {t.rank}</div>
                      <div className="text-[9px] uppercase tracking-[0.25em] text-[#FF1B91] font-bold">{t.medal}</div>
                    </div>
                  </div>
                </div>

                <h3 className="font-display font-black uppercase text-2xl lg:text-3xl tracking-tighter mb-2 leading-none">
                  {t.name}
                </h3>
                <p className="font-serif-italic text-base text-white/80 mb-5 leading-snug min-h-[3rem]">
                  {t.tagline}
                </p>

                <div className="border-y border-white/10 py-4 mb-5">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display font-black text-4xl text-white">{t.price}</span>
                    {!t.isBespoke && <span className="text-xs uppercase tracking-[0.2em] text-white/50">/ month</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF1B91] mt-2 font-semibold">{t.daily}</div>
                </div>

                <p className="text-white/70 leading-relaxed mb-5 text-sm">{t.desc}</p>

                <ul className="space-y-2 mb-5 text-sm text-white/85 flex-1">
                  {t.includes.map((b, i) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <CheckCircle size={14} weight="fill" className="text-[#FF1B91] flex-shrink-0 mt-1" />
                      <span className={i === 0 && b.endsWith("plus:") ? "font-semibold text-white" : ""}>{b}</span>
                    </li>
                  ))}
                </ul>

                {t.excludes.length > 0 && (
                  <ul className="space-y-2 mb-5 text-xs text-white/40">
                    {t.excludes.map((b) => (
                      <li key={b} className="flex items-start gap-2.5">
                        <XCircle size={12} weight="bold" className="flex-shrink-0 mt-0.5" />
                        <span className="line-through">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="border-t border-white/10 pt-4 mb-5">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-white/50 font-semibold mb-2">Perfect for</div>
                  <p className="text-xs text-white/70 leading-relaxed">{t.perfectFor}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <a
                    href={INTAKE_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`tier-cta-intake-${t.id}`}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-all ${
                      t.accent
                        ? "bg-[#FF1B91] hover:bg-[#FF3FA3] text-white btn-brutal btn-brutal-pink"
                        : t.isBespoke
                        ? "bg-white text-black hover:bg-[#FF1B91] hover:text-white"
                        : "bg-white text-black hover:bg-[#FF1B91] hover:text-white"
                    }`}
                  >
                    {t.isBespoke ? "Get a quote" : "Start intake"} <ArrowUpRight size={12} weight="bold" />
                  </a>
                  <a
                    href={EVERFIT_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`tier-cta-everfit-${t.id}`}
                    className="inline-flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white/60 hover:text-[#FF1B91] transition-colors"
                  >
                    Or join Everfit directly →
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-10 text-center text-xs uppercase tracking-[0.3em] text-white/40">
        All plans delivered via Everfit · Workout · Fuel · Focus · Repeat
      </p>
    </section>
  );
}
